import { createContext, useContext, type ReactNode } from 'react';
import { useFilterDraft, type FilterDraft, type UseFilterDraftOptions } from './useFilterDraft';

const FilterDraftContext = createContext<FilterDraft | null>(null);

/**
 * Owns the ONE `useFilterDraft` instance for a table (GTMOPS-2216: the draft
 * must outlive the popover) — hoisted OUT of `FlowTable`'s render (Phase 4).
 *
 * Why: `FlowTable` used to call `useFilterDraft` directly in its own render
 * body, so `setDraftFilters` (fired on every keystroke into a filter value
 * input) re-rendered `FlowTable` itself — and with it, the entire table body
 * (up to ~500 rows, no memoization anywhere in `components/flow/*.tsx`).
 * Moving the hook into this provider, rendered as `FlowTable`'s ROOT element
 * wrapping its existing JSX as `children`, fixes that: `children` is created
 * by `FlowTable`'s OWN render and passed down as a prop, so a state update
 * inside `FilterDraftProvider` re-renders only the provider and whichever
 * descendants read the context (`FilterPopover`, `FilterDraftBox`) — React
 * bails out of re-rendering the unchanged `children` subtree, which is where
 * the table body actually lives. One keystroke into a filter value no longer
 * touches a single row.
 */
export function FilterDraftProvider({ children, ...options }: UseFilterDraftOptions & { children: ReactNode }) {
  const draft = useFilterDraft(options);
  return <FilterDraftContext.Provider value={draft}>{children}</FilterDraftContext.Provider>;
}

/** The provider's draft, or null when rendered outside one (mini-app direct mounts of FilterPopover). */
export function useFilterDraftContext(): FilterDraft | null {
  return useContext(FilterDraftContext);
}
